import Button from "app/storybookComponents/Button";
import { Fragment, useCallback, useState, ComponentType } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers } from "@fortawesome/pro-regular-svg-icons";
import {
  ReportMode,
  ReportSettings,
  ReportSummary,
  UpdatePayload,
} from "app/containers/WeeklyCheckInMonitor/types";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "app/shadcnComponents/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
} from "app/shadcnComponents/ui/select";
import { Skeleton } from "app/shadcnComponents/ui/skeleton";
import { cn } from "app/lib/utils";

const THRESHOLD_OPTIONS = [
  { value: "25", label: "25%" },
  { value: "50", label: "50%" },
  { value: "75", label: "75%" },
  { value: "100", label: "100%" },
];

const DEFAULT_MODE: ReportMode = "all_who_qualify";
const DEFAULT_THRESHOLD = 50;

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onEditSettings?: () => void;
}

interface ReportSettingsSectionProps {
  settings: ReportSettings | undefined;
  isLoading: boolean;
  isError: boolean;
  preview: { summary: ReportSummary } | undefined;
  isPreviewLoading: boolean;
  isSaving: boolean;
  updateSettings: (payload: UpdatePayload) => void;
  reportName: string;
  thresholdCopyText: string;
  PreviewDrawer: ComponentType<DrawerProps>;
}

const ReportSettingsSection = ({
  settings,
  isLoading,
  isError,
  preview,
  isPreviewLoading,
  isSaving,
  updateSettings,
  reportName,
  thresholdCopyText,
  PreviewDrawer,
}: Readonly<ReportSettingsSectionProps>) => {
  const [isPreviewDrawerOpen, setIsPreviewDrawerOpen] = useState(false);

  const currentMode = settings?.mode ?? DEFAULT_MODE;
  const currentThreshold = settings?.thresholdPercentage ?? DEFAULT_THRESHOLD;

  const dispatchUpdate = useCallback(
    (overrides: { mode?: ReportMode; thresholdPercentage?: number | null }) => {
      const mode = overrides.mode ?? currentMode;
      const thresholdPercentage =
        overrides.thresholdPercentage ?? currentThreshold;

      updateSettings({
        mode,
        thresholdPercentage:
          mode === "all_who_qualify" ? thresholdPercentage : null,
        excludedUserAccountIds:
          settings?.excludedUsers.map((u) => u.userAccountId) ?? [],
        includedUserAccountIds:
          settings?.includedUsers.map((u) => u.userAccountId) ?? [],
      });
    },
    [updateSettings, currentMode, currentThreshold, settings]
  );

  const handleModeChange = (mode: string) => {
    dispatchUpdate({ mode: mode as ReportMode });
  };

  const handleThresholdChange = (value: string) => {
    dispatchUpdate({ thresholdPercentage: Number(value) });
  };

  const getSettingBody = () => {
    if (isLoading) {
      return (
        <div className="mt-3 flex flex-col gap-3">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-8 w-64" />
        </div>
      );
    }

    return (
      <div className={cn("pt-1", isSaving && "pointer-events-none opacity-70")}>
        <Tabs
          value={currentMode}
          onValueChange={handleModeChange}
          activationMode="manual"
        >
          <TabsList className="mb-3">
            <TabsTrigger value="specific_people">Specific people</TabsTrigger>
            <TabsTrigger value="all_who_qualify">All who qualify</TabsTrigger>
          </TabsList>

          <TabsContent value="specific_people">
            <p className="text-xs text-charcoal-50 mb-3">
              Only people you add will receive the {reportName}. Use
              &ldquo;Manage recipients&rdquo; to add or remove people.
            </p>
          </TabsContent>

          <TabsContent value="all_who_qualify">
            <div className="flex items-center gap-2 text-sm text-charcoal-90 mb-3 flex-wrap">
              <span>Send when at least</span>
              <Select
                value={String(currentThreshold)}
                onValueChange={handleThresholdChange}
                disabled={isSaving}
              >
                <SelectTrigger className="w-[80px] h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {THRESHOLD_OPTIONS.map((opt, idx) => (
                    <Fragment key={opt.value}>
                      <SelectItem value={opt.value}>{opt.label}</SelectItem>
                      {idx < THRESHOLD_OPTIONS.length - 1 && (
                        <SelectSeparator />
                      )}
                    </Fragment>
                  ))}
                </SelectContent>
              </Select>
              <span>{thresholdCopyText}</span>
            </div>
          </TabsContent>
        </Tabs>

        {/* Recipients summary banner */}
        {isPreviewLoading && (
          <div className="mt-3 p-4 bg-charcoal-02 border border-charcoal-20 rounded-lg flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex flex-col gap-1.5">
                <Skeleton className="h-6 w-8" />
                <Skeleton className="h-3 w-16" />
              </div>
              {currentMode === "all_who_qualify" && (
                <>
                  <div className="w-px h-8 bg-charcoal-20" />
                  <div className="flex flex-col gap-1.5">
                    <Skeleton className="h-6 w-8" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                  <div className="w-px h-8 bg-charcoal-20" />
                  <div className="flex flex-col gap-1.5">
                    <Skeleton className="h-6 w-8" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </>
              )}
            </div>
            <Skeleton className="h-9 w-40 rounded-lg" />
          </div>
        )}
        {preview?.summary && (
          <div className="mt-3 p-4 bg-charcoal-02 border border-charcoal-20 rounded-lg flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex flex-col gap-0.5">
                <span className="text-xl font-bold leading-6">
                  {preview.summary.qualifiedByRules +
                    (preview.summary.includedOverride ?? 0)}
                </span>
                <span className="text-[11px] font-medium uppercase tracking-wider">
                  Receiving
                </span>
              </div>
              {currentMode === "all_who_qualify" && (
                <>
                  <div className="w-px h-8 bg-charcoal-20" />
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xl font-bold leading-6 text-charcoal-80">
                      {preview.summary.excluded}
                    </span>
                    <span className="text-[11px] font-medium text-charcoal-80 uppercase tracking-wider">
                      Manually Excluded
                    </span>
                  </div>
                  <div className="w-px h-8 bg-charcoal-20" />
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xl font-bold leading-6 text-charcoal-60">
                      {preview.summary.belowThreshold}
                    </span>
                    <span className="text-[11px] font-medium text-charcoal-60 uppercase tracking-wider">
                      Excluded by threshold
                    </span>
                  </div>
                </>
              )}
            </div>
            <Button
              variant="secondary-gray"
              onClick={() => setIsPreviewDrawerOpen(true)}
              className="hover:!bg-sapphire-10 hover:!text-charcoal-90"
            >
              <FontAwesomeIcon
                icon={faUsers}
                className="text-sapphire-100 text-xs mr-2"
              />
              <span className="text-black">Manage recipients</span>
            </Button>
          </div>
        )}
        {!preview?.summary && !isPreviewLoading && (
          <button
            className="mt-3 flex items-center gap-1.5 text-sm cursor-pointer bg-transparent border-none p-0"
            onClick={() => setIsPreviewDrawerOpen(true)}
          >
            <FontAwesomeIcon
              icon={faUsers}
              className="text-charcoal-50 text-xs"
            />
            <span className="text-sapphire-100 font-medium text-sm hover:underline">
              Manage recipients
            </span>
          </button>
        )}
      </div>
    );
  };

  if (isError) {
    return null;
  }

  return (
    <div className={cn("mt-1", isSaving && "cursor-wait")}>
      {getSettingBody()}

      <PreviewDrawer
        isOpen={isPreviewDrawerOpen}
        onClose={() => setIsPreviewDrawerOpen(false)}
        onEditSettings={() => setIsPreviewDrawerOpen(false)}
      />
    </div>
  );
};

export default ReportSettingsSection;
